import {
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import {
  initialPageReviewCount,
  minReviewsToDisplay
} from '~/constants/vdp/ownerReviews';
import type { OwnerReview, VehicleRating } from '~/contracts/vdp';
import type { OwnerReviewsResponse } from '~/services/vdp/contracts';
import calculateRatingsAndDistribution from '~/utilities/vdp/calculateRatingsAndDistribution';
import {
  mapReviewProperties,
  sanitizeReviews
} from '~/utilities/vdp/ownerReviews';

type DrawerOptions = {
  isInputAnchored: boolean;
  isInputFocused: boolean;
};

type ContextType = {
  drawerOptions: DrawerOptions;
  drawerReviewCount: number;
  drawerReviews: ReturnType<typeof useLoadableState<OwnerReview[]>>;
  fetchOwnerReviewsFlag: number;
  hasMoreReviews: boolean;
  hasOwnerReviews: ReturnType<typeof useLoadableState<boolean>>;
  pageNumber: number;
  pageReviewCount: number;
  pageReviews: ReturnType<typeof useLoadableState<OwnerReview[]>>;
  ratingData: ReturnType<typeof useLoadableState<VehicleRating>>;
  resetDrawerOptions: () => void;
  searchTerm: string;
  selectedReview: string | undefined;
  setDrawerOptions: (options: DrawerOptions) => void;
  setDrawerReviewCount: (count: number) => void;
  setInitialReviewsData: (data: OwnerReviewsResponse) => void;
  setPageNumber: (page: number) => void;
  setPageReviewCount: (count: number) => void;
  setSearchTerm: (term: string) => void;
  setSelectedReviewId: (id?: string) => void;
  setStarRatingFilter: (rating?: number) => void;
  starRatingFilter: number | undefined;
  triggerFetchOwnerReviews: () => void;
};

export const defaultContext: ContextType = {
  drawerOptions: {
    isInputAnchored: false,
    isInputFocused: false
  },
  drawerReviewCount: 0,
  drawerReviews: defaultLoadableState<OwnerReview[]>([]),
  fetchOwnerReviewsFlag: 0,
  hasMoreReviews: false,
  hasOwnerReviews: defaultLoadableState<boolean>(false),
  pageNumber: 1,
  pageReviewCount: 0,
  pageReviews: defaultLoadableState<OwnerReview[]>([]),
  ratingData: defaultLoadableState<VehicleRating>({
    ratingDistribution: [],
    totalRatingCounts: 0
  }),
  resetDrawerOptions: () => {},
  searchTerm: '',
  selectedReview: undefined as string | undefined,
  setDrawerOptions: (_: DrawerOptions) => {},
  setDrawerReviewCount: (_: number) => {},
  setInitialReviewsData: (_: OwnerReviewsResponse) => {},
  setPageNumber: (_: number) => {},
  setPageReviewCount: (_: number) => {},
  setSearchTerm: (_: string) => {},
  setSelectedReviewId: (_?: string) => {},
  setStarRatingFilter: (_: number | undefined) => {},
  starRatingFilter: undefined as number | undefined,
  triggerFetchOwnerReviews: () => {}
};

const StarRatingOwnerReviewContext = createContext<ContextType>(defaultContext);

export function StarRatingOwnerReviewContextProvider({
  children
}: React.PropsWithChildren<{}>) {
  const [drawerOptions, setDrawerOptions] = useState<DrawerOptions>(
    defaultContext.drawerOptions
  );
  const [drawerReviewCount, setDrawerReviewCount] = useState(
    defaultContext.drawerReviewCount
  );
  const drawerReviews = useLoadableState<OwnerReview[]>([]);
  const [fetchOwnerReviewsFlag, setFetchOwnerReviewsFlag] = useState(
    defaultContext.fetchOwnerReviewsFlag
  );
  const hasOwnerReviews = useLoadableState<boolean>(
    defaultContext.hasOwnerReviews.data
  );
  const [pageNumber, setPageNumber] = useState(defaultContext.pageNumber);
  const [pageReviewCount, setPageReviewCount] = useState(
    defaultContext.pageReviewCount
  );
  const pageReviews = useLoadableState<OwnerReview[]>([]);
  const ratingData = useLoadableState<VehicleRating>({
    ratingDistribution: [],
    totalRatingCounts: 0
  });
  const [searchTerm, setSearchTerm] = useState(defaultContext.searchTerm);
  const [selectedReview, setSelectedReviewId] = useState(
    defaultContext.selectedReview
  );
  const [starRatingFilter, setStarRatingFilter] = useState(
    defaultContext.starRatingFilter
  );

  const resetDrawerOptions = useCallback(() => {
    setDrawerOptions(defaultContext.drawerOptions);
  }, []);

  // No searchTerm/highlighting here: this only ever runs on the very first (unfiltered)
  // load, before a search term can exist, so it stays stable across renders. Live search
  // re-fetches apply highlighting inline in useGetReviews.ts instead of through this setter.
  const setInitialReviewsData = useCallback(
    (data: OwnerReviewsResponse) => {
      const reviews = sanitizeReviews(mapReviewProperties(data.reviews));
      const ratingDistributions = calculateRatingsAndDistribution(
        data.summary.rating_counts,
        reviews
      );

      drawerReviews.setData.call(undefined, reviews);
      hasOwnerReviews.setData.call(
        undefined,
        ratingDistributions.totalRatingCounts > minReviewsToDisplay
      );
      pageReviews.setData.call(
        undefined,
        reviews.slice(0, initialPageReviewCount)
      );
      ratingData.setData.call(undefined, ratingDistributions);
      setDrawerReviewCount(data.summary.review_count_total);
      setPageReviewCount(data.summary.review_count_total);
    },
    [
      drawerReviews.setData,
      hasOwnerReviews.setData,
      pageReviews.setData,
      ratingData.setData
    ]
  );

  const triggerFetchOwnerReviews = useCallback(
    () => setFetchOwnerReviewsFlag(current => current + 1),
    []
  );

  const value = useMemo<ContextType>(
    () => ({
      drawerOptions,
      drawerReviewCount,
      drawerReviews,
      fetchOwnerReviewsFlag,
      hasMoreReviews: drawerReviews.data.length < drawerReviewCount,
      hasOwnerReviews,
      pageNumber,
      pageReviewCount,
      pageReviews,
      ratingData,
      resetDrawerOptions,
      searchTerm,
      selectedReview,
      setDrawerOptions,
      setDrawerReviewCount,
      setInitialReviewsData,
      setPageNumber,
      setPageReviewCount,
      setSearchTerm,
      setSelectedReviewId,
      setStarRatingFilter,
      starRatingFilter,
      triggerFetchOwnerReviews
    }),
    [
      drawerOptions,
      drawerReviewCount,
      drawerReviews,
      fetchOwnerReviewsFlag,
      hasOwnerReviews,
      pageNumber,
      pageReviewCount,
      pageReviews,
      ratingData,
      resetDrawerOptions,
      searchTerm,
      selectedReview,
      setInitialReviewsData,
      starRatingFilter,
      triggerFetchOwnerReviews
    ]
  );

  return (
    <StarRatingOwnerReviewContext.Provider value={value}>
      {children}
    </StarRatingOwnerReviewContext.Provider>
  );
}

StarRatingOwnerReviewContextProvider.displayName =
  'StarRatingOwnerReviewContextProvider';

export function useHasOwnerReviews() {
  const { hasOwnerReviews } = useContext(StarRatingOwnerReviewContext);

  return hasOwnerReviews.data;
}

export default StarRatingOwnerReviewContext;
